import {
  LOADOUT_DURATION_MS,
  LOADOUT_READY_GRACE_MS,
  MATCH_END_DURATION_MS,
  MIN_PLAYERS_TO_START,
  ROUND_DURATION_MS,
  ROUND_END_DURATION_MS,
  ROUNDS_TO_WIN,
} from './constants.js';
import type { PlayerId, TeamId } from './types.js';

/**
 * The match lifecycle. Only `live` simulates gameplay — in every other phase the
 * server freezes players at their spawns, and the client freezes identically so
 * prediction still agrees with authority (see the freeze in `GameEngine.tick`).
 *
 *   waiting   too few players; nothing is running
 *   loadout   everyone picks a weapon against a countdown
 *   live      a round is being played; ends on a team wipe or at the buzzer
 *             (the wipe is the server pulling the deadline forward — this pure
 *             machine still only ever leaves `live` on an expired clock)
 *   roundEnd  the between-rounds banner — who took the round, then back to live
 *   ended     the game is decided; the rematch window runs MATCH_END_DURATION_MS
 */
export type MatchPhase = 'waiting' | 'loadout' | 'live' | 'ended' | 'roundEnd';

/** Wire encoding of `MatchPhase`. Order is part of the protocol. */
export const MATCH_PHASES: readonly MatchPhase[] = [
  'waiting',
  'loadout',
  'live',
  'ended',
  'roundEnd',
];

export const matchPhaseIndex = (phase: MatchPhase): number =>
  MATCH_PHASES.indexOf(phase);

export const matchPhaseFromIndex = (index: number): MatchPhase =>
  MATCH_PHASES[index] ?? 'waiting';

export interface PhaseInput {
  phase: MatchPhase;
  /** Everyone on the roster, bots included — bots are real combatants. */
  playerCount: number;
  /**
   * Humans on the roster. Bots count toward `playerCount` and `teamCounts`
   * (they can hold a side and be fought), but a match with no humans left in
   * it is playing for nobody and must collapse — bots never keep a match
   * alive on their own.
   */
  humanCount: number;
  /**
   * Connected players per team. A side with nobody *online* is different from a
   * wiped side (dead players are still on the roster): once a whole team leaves
   * there is no opponent left to play, so the match collapses immediately
   * instead of running out the clock against nobody.
   */
  teamCounts: [number, number];
  /** Milliseconds left in the current phase. `Infinity` when there is no deadline. */
  remainingMs: number;
  /**
   * Round wins of the leading team, already updated for the round that just
   * finished — the caller scores the round *before* asking for a transition.
   */
  topRoundWins: number;
}

export interface PhaseTransition {
  phase: MatchPhase;
  /** 0 means the new phase has no deadline and waits on players instead. */
  durationMs: number;
}

/**
 * The whole match lifecycle as a pure function, so the rules can be tested
 * without a socket, a clock or a running server. Returns the phase to move to,
 * or null to stay put.
 *
 * Note that "everyone is ready" is deliberately *not* an input here: readiness
 * shortens the countdown (see `readyCountdownMs`) rather than starting the round
 * directly. That is what makes a stalling player harmless — the deadline is the
 * only thing that ever starts a round, and readiness just brings it forward.
 */
export const nextMatchPhase = (input: PhaseInput): PhaseTransition | null => {
  // Enough players *and* an opponent to play them against. Head count alone is
  // not it: a 2v1 whose lone defender disconnects still has two players online,
  // but nobody left to fight — the match must collapse, not run out the clock.
  // At least one human must remain: an all-bot match is playing for nobody.
  const viable =
    input.playerCount >= MIN_PLAYERS_TO_START &&
    input.humanCount > 0 &&
    input.teamCounts[0] > 0 &&
    input.teamCounts[1] > 0;
  const expired = input.remainingMs <= 0;

  switch (input.phase) {
    case 'waiting':
      return viable ? { phase: 'loadout', durationMs: LOADOUT_DURATION_MS } : null;

    case 'loadout':
      if (!viable) return { phase: 'waiting', durationMs: 0 };
      return expired ? { phase: 'live', durationMs: ROUND_DURATION_MS } : null;

    case 'live':
      if (!viable) return { phase: 'waiting', durationMs: 0 };
      if (!expired) return null;
      // The round just got scored by the caller. Someone at the target ends the
      // game; otherwise a short banner, then the next round.
      return input.topRoundWins >= ROUNDS_TO_WIN
        ? { phase: 'ended', durationMs: MATCH_END_DURATION_MS }
        : { phase: 'roundEnd', durationMs: ROUND_END_DURATION_MS };

    case 'roundEnd':
      if (!viable) return { phase: 'waiting', durationMs: 0 };
      return expired ? { phase: 'live', durationMs: ROUND_DURATION_MS } : null;

    case 'ended':
      // A rematch needs a full lobby; once anyone leaves there is no point
      // making the rest sit out the window, so the collapse is immediate.
      if (!viable) return { phase: 'waiting', durationMs: 0 };
      return expired ? { phase: 'loadout', durationMs: LOADOUT_DURATION_MS } : null;
  }
};

/**
 * Countdown remaining once every player has locked in. Never extends the
 * deadline — a lobby that is already about to start does not get longer because
 * the last player picked late.
 */
export const readyCountdownMs = (remainingMs: number): number =>
  Math.min(remainingMs, LOADOUT_READY_GRACE_MS);

// ---------------------------------------------------------------------------
// Round scoring — the whole mode in one pure function
// ---------------------------------------------------------------------------

/** Who took a round: a team id, or `ROUND_DRAW`. */
export type RoundResult = TeamId | typeof ROUND_DRAW;

export const ROUND_DRAW = 2;
/** Wire value for "no round has finished yet". */
export const ROUND_RESULT_NONE = 255;

/**
 * When the round clock runs out, each team's health points are summed — dead
 * players count zero — and the lower total loses the round. Equal totals are a
 * draw, which nobody scores. Kills only matter insofar as they take health off
 * the board.
 */
export const scoreRound = (blueHealth: number, redHealth: number): RoundResult => {
  if (blueHealth === redHealth) return ROUND_DRAW;
  return blueHealth > redHealth ? 0 : 1;
};

// ---------------------------------------------------------------------------
// Lobby pads — pure rules, mirrored by the server's pad controller
// ---------------------------------------------------------------------------

/**
 * One side of a pad group is cleared to start when it is full, or when every
 * player standing on it has pressed Ready to fight outnumbered. An empty side
 * is never cleared — a match needs someone on both pads.
 */
export const padSideCleared = (
  count: number,
  capacity: number,
  readyCount: number,
): boolean => count > 0 && (count >= capacity || readyCount >= count);

/**
 * The whole group starts its countdown when both sides are cleared. Note the
 * same principle as the loadout phase: Ready never starts a match by itself —
 * it only arms the countdown, and the countdown is the only thing that starts
 * anything.
 */
export const padGroupCleared = (
  sides: readonly { count: number; capacity: number; readyCount: number }[],
): boolean =>
  sides.length > 0 &&
  sides.every((side) => padSideCleared(side.count, side.capacity, side.readyCount));

/**
 * Weighted map pick. Each vote is a ticket in the draw: two players on one map
 * against one on another gives 2/3 vs 1/3, a straight split is 50/50, and with
 * no votes at all every map is equally likely. `roll` is a uniform [0, 1) —
 * passed in rather than drawn here so the rule itself stays deterministic and
 * testable.
 */
export const chooseMapIndex = (voteCounts: readonly number[], roll: number): number => {
  const total = voteCounts.reduce((sum, count) => sum + count, 0);
  if (total <= 0) {
    return Math.min(voteCounts.length - 1, Math.floor(roll * voteCounts.length));
  }
  let cumulative = 0;
  const target = roll * total;
  for (let index = 0; index < voteCounts.length; index++) {
    cumulative += voteCounts[index]!;
    if (target < cumulative) return index;
  }
  return voteCounts.length - 1;
};

/** One pad group's queue state, broadcast to everyone in the lobby. */
export interface HubGroupStatus {
  /** Milliseconds until the match launches; 0 when no countdown is running. */
  countdownEndsInMs: number;
  /** Player ids standing on each side, indexed by team. */
  teams: [PlayerId[], PlayerId[]];
  votes: { id: PlayerId; mapIndex: number }[];
  /** Bot-difficulty votes, tier as an index into BOT_TIERS. Vs-bot pads only. */
  difficultyVotes: { id: PlayerId; tier: number }[];
  ready: PlayerId[];
}

/** One player as they appear in the lobby roster. */
export interface LobbyEntry {
  id: PlayerId;
  team: TeamId;
  /**
   * In the loadout lobby: locked in a weapon. In `ended`: pressed Rematch.
   * The same flag on the wire, because it means the same thing — "waiting on
   * everyone else".
   */
  ready: boolean;
}

/**
 * Everything the client needs to render the lobby, the countdown and the map
 * banner. Broadcast on every phase change and once a second while a countdown is
 * running — never per tick.
 */
export interface MatchStatus {
  phase: MatchPhase;
  /**
   * Milliseconds until the current phase ends, or 0 when it has no deadline
   * (`waiting`). Sent as a duration rather than a timestamp so no clock sync is
   * needed between the server and the client.
   */
  phaseEndsInMs: number;
  mapId: string;
  /** Team kill totals for the game, indexed by team id. */
  scores: [number, number];
  /** Round wins per team. First to ROUNDS_TO_WIN takes the game. */
  rounds: [number, number];
  /** The round being played, 1-based. Draws still advance it. */
  roundNumber: number;
  /** Result of the round that just finished, or ROUND_RESULT_NONE. */
  lastRoundResult: number;
  roster: LobbyEntry[];
}
